import React, { useEffect } from 'react'

interface HelpModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
	if (!isOpen) return null

	useEffect(() => {
		const keyHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		document.addEventListener('keydown', keyHandler)

		return () => document.removeEventListener('keydown', keyHandler)
	}, [])

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="help-modal" onClick={(e) => e.stopPropagation()}>
				<div className="help-header">
					<h2>Welcome to Memtastic!</h2>
					<button className="close-button" onClick={onClose}>×</button>
				</div>

				<div className="help-content">
					<div className="help-section">
						<h3>🎵 Getting Started</h3>
						<p>This webapp helps children and adults memorize Primary songs, or any other song or text, through interactive word hiding.</p>
					</div>

					<div className="help-section">
						<h3>📝 Creating Songs</h3>
						<ul>
							<li>Click "Select Song" to open the song picker</li>
							<li>Click "Create New Song" to add lyrics</li>
							<li>Paste or type song lyrics line by line</li>
							<li>Give your song a memorable name</li>
						</ul>
					</div>

					<div className="help-section">
						<h3>🎮 Using the Controls</h3>
						<ul>
							<li><strong>Visibility Slider:</strong> Control what percentage of words are shown (0-100%)</li>
							<li><strong>Font Size:</strong> Adjust text size for better readability</li>
							<li><strong>Shuffle:</strong> Randomize which words are hidden at current percentage</li>
							<li><strong>Display Window:</strong> Open a full-screen view perfect for TVs or projectors</li>
						</ul>
					</div>

					<div className="help-section">
						<h3>✨ Interactive Features</h3>
						<ul>
							<li>Click any word to toggle between hidden/visible</li>
							<li>Hidden words show colorful backgrounds with first letters</li>
							<li>Visible words have transparent backgrounds</li>
							<li>Both windows stay perfectly synchronized</li>
						</ul>
					</div>

					<div className="help-section">
						<h3>💡 Learning Tips</h3>
						<ul>
							<li>Start with 100% visibility, then gradually decrease</li>
							<li>Use shuffle to practice different word combinations</li>
							<li>Click individual words to choose what to hide and show - it can be fun to have the kids choose which words to hide.</li>
							<li>Use the display window for group singing activities, and project it onto a TV screen</li>
							<li>Start with easier modes, then move to harder ones (see the next section)</li>
							<li>Add emojis to make learning fun and help it to stick! See the Example Song to understand the format.</li>
						</ul>
					</div>

					<div className="help-section">
						<h3>🎯 Customizing Display Modes</h3>
						<p>Fine-tune what's shown for visible and hidden words to optimize learning:</p>
						<ul>
							<li><strong>Visible Word Display:</strong>
								<ul>
									<li><em>Show word:</em> Display the complete word text</li>
									<li><em>Show emoji/symbol:</em> Only show symbols (🎵, ❤️, etc.) if present</li>
									<li><em>Show both:</em> Display both word text and symbols together</li>
									<li><em>Show first letter:</em> Only show the first letter of each word</li>
								</ul>
							</li>
							<li><strong>Hidden Word Display:</strong>
								<ul>
									<li><em>Hide completely:</em> Show colorful background with no text hints</li>
									<li><em>Show first letter:</em> Reveal just the first letter as a memory aid</li>
									<li><em>Show emoji/symbol:</em> Display only symbols while hiding word text</li>
								</ul>
							</li>
						</ul>
					</div>
				</div>

				<div className="help-footer">
					<button className="primary-button" onClick={onClose}>
						Got it, let's start!
					</button>
				</div>
			</div>
		</div>
	)
}
